import { Component } from '@theme/component';

/**
 * Get started checkout: turns a plan link from the intake form
 * (/pages/get-started?tier=premier&ref=…) into a cart and sends the visitor
 * to checkout.
 *
 * Each plan is rendered by Liquid (sections/sb-get-started.liquid) and hidden;
 * this shows the one named by ?tier=. On continue it empties the cart, adds
 * the plan's setup product and its membership with the chosen subscription
 * term, tags the cart with the intake reference (so the order can be matched
 * to the submission) and the chosen term's name, and opens checkout. With
 * ?redesign=1 it also shows and adds the existing store redesign fee, when the
 * plan has one. With ?locations=2–5 (one store per location) each item is
 * added that many times. With ?hours=N&rate=member|standard it shows the
 * support hours product at that rate instead of a plan, and adds N hours.
 *
 * @typedef {object} GetStartedRefs
 * @property {HTMLElement[]} [plans] - One element per plan, with data-tier (none if no products are set)
 * @property {HTMLElement} missing - Shown when ?tier= matches no plan
 * @property {HTMLElement} error - Shown when the cart can't be prepared
 * @property {HTMLElement} [intro] - The plan intro, hidden when buying support hours
 *
 * @extends {Component<GetStartedRefs>}
 */
class SbGetStartedComponent extends Component {
  requiredRefs = ['missing', 'error'];

  /** Whether the existing store redesign fee goes into the cart. */
  redesign = false;

  /** How many of each item go into the cart: one store per location, 1–5. */
  locations = 1;

  /** Support hours being bought (1–20), and the variant for the client's rate. */
  hours = 0;
  hoursVariant = 0;

  connectedCallback() {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    this.reference = params.get('ref') ?? '';
    const hours = Number.parseInt(params.get('hours') ?? '', 10);
    const tier = hours > 0 ? 'support_hours' : params.get('tier');
    const plan = (this.refs.plans ?? []).find((element) => element.dataset.tier === tier);
    const rateRow = plan?.querySelector(`[data-rate-sku="SUPPORT-HOURS-${String(params.get('rate')).toUpperCase()}"]`);

    if (!plan || (tier === 'support_hours' && !(rateRow instanceof HTMLElement))) {
      this.refs.missing.hidden = false;
      return;
    }

    plan.hidden = false;

    if (tier === 'support_hours' && rateRow instanceof HTMLElement) {
      this.hours = Math.min(hours, 20);
      this.hoursVariant = Number(rateRow.dataset.variant);
      rateRow.hidden = false;
      if (this.refs.intro) this.refs.intro.hidden = true;
      const count = plan.querySelector('[data-hours-count]');
      if (count) count.textContent = String(this.hours);
      return;
    }

    const locations = Number.parseInt(params.get('locations') ?? '1', 10);
    if (locations > 1 && locations <= 5 && plan.dataset.tier !== 'info_only') {
      this.locations = locations;
      const row = plan.querySelector('[data-locations]');
      const count = plan.querySelector('[data-locations-count]');
      if (row instanceof HTMLElement && count) {
        count.textContent = `× ${locations}`;
        row.hidden = false;
      }
    }

    this.redesign = params.get('redesign') === '1' && Boolean(plan.dataset.redesignVariant);
    if (this.redesign) {
      plan.querySelectorAll('[data-redesign]').forEach((element) => {
        if (element instanceof HTMLElement) element.hidden = false;
      });
    }
  }

  /**
   * Builds the cart for the plan whose button was pressed, then opens checkout.
   * @param {MouseEvent} event
   */
  async checkout(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const plan = button.closest('[data-tier]');
    if (!(plan instanceof HTMLElement)) return;

    if (plan.dataset.tier === 'support_hours') {
      await this.#fillCart(button, [{ id: this.hoursVariant, quantity: this.hours }], {
        'Intake reference': this.reference,
        Purchase: 'Support hours',
      });
      return;
    }

    const term = plan.querySelector('input[type="radio"]:checked');
    const quantity = this.locations;
    const items = [{ id: Number(plan.dataset.setupVariant), quantity }];
    if (plan.dataset.membershipVariant) {
      items.push({
        id: Number(plan.dataset.membershipVariant),
        quantity,
        ...(term instanceof HTMLInputElement && { selling_plan: Number(term.value) }),
      });
    }
    if (this.redesign) items.push({ id: Number(plan.dataset.redesignVariant), quantity });

    await this.#fillCart(button, items, {
      'Intake reference': this.reference,
      Plan: plan.dataset.tier,
      Locations: String(quantity),
      'Membership term': term instanceof HTMLInputElement ? (term.dataset.term ?? '') : '',
    });
  }

  /**
   * Replaces the cart with `items`, tags it with `attributes`, and opens checkout.
   * @param {HTMLButtonElement} button
   * @param {object[]} items
   * @param {Record<string, string>} attributes
   */
  async #fillCart(button, items, attributes) {
    button.disabled = true;
    this.refs.error.hidden = true;

    try {
      await this.#post(`${Theme.routes.cart_url}/clear.js`, {});
      await this.#post(Theme.routes.cart_add_url, { items });
      await this.#post(`${Theme.routes.cart_update_url}.js`, { attributes });
      window.location.assign('/checkout');
    } catch (error) {
      console.error(error);
      this.refs.error.hidden = false;
      button.disabled = false;
    }
  }

  /**
   * @param {string} url
   * @param {object} body
   */
  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${url} failed: ${response.status}`);
    return response.json();
  }
}

if (!customElements.get('sb-get-started-component')) {
  customElements.define('sb-get-started-component', SbGetStartedComponent);
}
